import { useCallback, useEffect, useRef, useState } from 'react';

export const COMPOSE_DRAFT_AUTOSAVE_INTERVAL_SEC = 30;

export function formatDraftAutosaveCountdown(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  if (s >= 60) {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  }
  return `0:${s.toString().padStart(2, '0')}`;
}

export type ComposeDraftAutosaveStatus = 'idle' | 'saving' | 'error';

export function useComposeDraftAutosave({
  enabled,
  intervalSec = COMPOSE_DRAFT_AUTOSAVE_INTERVAL_SEC,
  onAutosave,
}: {
  enabled: boolean;
  intervalSec?: number;
  onAutosave: () => Promise<boolean>;
}) {
  const [secondsUntilSave, setSecondsUntilSave] = useState(intervalSec);
  const [status, setStatus] = useState<ComposeDraftAutosaveStatus>('idle');
  const onAutosaveRef = useRef(onAutosave);
  onAutosaveRef.current = onAutosave;
  const saveInFlightRef = useRef(false);

  const resetCountdown = useCallback(() => {
    setSecondsUntilSave(intervalSec);
    setStatus('idle');
  }, [intervalSec]);

  useEffect(() => {
    if (!enabled) {
      setSecondsUntilSave(intervalSec);
      setStatus('idle');
      return;
    }
    setSecondsUntilSave(intervalSec);
    setStatus('idle');
    const tick = window.setInterval(() => {
      setSecondsUntilSave((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(tick);
  }, [enabled, intervalSec]);

  useEffect(() => {
    if (!enabled || secondsUntilSave > 0 || saveInFlightRef.current) return;

    let cancelled = false;
    saveInFlightRef.current = true;
    setStatus('saving');

    void (async () => {
      try {
        const ok = await onAutosaveRef.current();
        if (cancelled) return;
        setStatus(ok ? 'idle' : 'error');
        setSecondsUntilSave(intervalSec);
      } finally {
        saveInFlightRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, secondsUntilSave, intervalSec]);

  return { secondsUntilSave, status, resetCountdown, setStatus };
}
